import type { MessageContent as MessageContentProps } from "@prelude-ai/design-system"
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtItem,
  ChainOfThoughtStep,
  ChainOfThoughtTrigger,
  ChatContainerContent,
  ChatContainerRoot,
  ChatContainerScrollAnchor,
  ClarificationSummary,
  cn,
  MessageContent as MessageContentComponent,
  Tool
} from "@prelude-ai/design-system"
import type { ComponentPropsWithoutRef } from "react"
import { useMemo } from "react"
import type { ToolCall } from "../../types"
import type { ChatMessage as ChatMessageType } from "../types"
import { isReasoningMessage } from "../utils/chat-state.ts"
import { ChatMessage } from "./ChatMessage"
import ToolCallMessage from "./ToolCallMessage"

export type MessageListProps = {
  className?: string
  messages: ChatMessageType[]
  currentResponse: string
  currentToolCall?: ToolCall
  isLoading?: boolean
  devMode: boolean
  currentThought?: string | null
}

type MessageProps = ComponentPropsWithoutRef<typeof MessageContentProps> & {
  isReasoning?: boolean
  userResponse?: boolean
}

const baseClassNames = "text-sm p-0 border-none bg-none overflow-x-auto max-w-full"
const userResponseClassNames = "bg-primary p-2 border-none text-primary-foreground"
const reasoningContentClassNames = "italic text-muted-foreground border-dashed border-border/50"

const MessageContent = ({ children, userResponse = false, isReasoning = false, className, ...rest }: MessageProps) => {
  return (
    <MessageContentComponent
      markdown
      className={cn(
        baseClassNames,
        userResponse && userResponseClassNames,
        isReasoning && reasoningContentClassNames,
        !userResponse && "bg-transparent prose prose-sm dark:prose-invert",
        className
      )}
      {...rest}
    >
      {children}
    </MessageContentComponent>
  )
}

function ThoughtSubtitle({ text }: { text: string }) {
  return <div className="text-sm text-muted-foreground italic truncate">{text}</div>
}

function ThoughtChain({ messages }: { messages: ChatMessageType[] }) {
  const displayMessages = messages.filter((m) => !/^Calling tools?:\s*(\w+)/.test(m.content))
  if (displayMessages.length === 0) return null
  return (
    <ChainOfThought>
      {displayMessages.map((message) => (
        <ChainOfThoughtStep key={message.id}>
          {message.isToolCall && message.toolName ? (
            <ToolCallMessage message={message} />
          ) : message.content.length > 100 ? (
            <>
              <ChainOfThoughtTrigger>{`${message.content.slice(0, 100)}...`}</ChainOfThoughtTrigger>
              <ChainOfThoughtContent>
                <ChainOfThoughtItem>{message.content}</ChainOfThoughtItem>
              </ChainOfThoughtContent>
            </>
          ) : (
            <ChainOfThoughtItem>{message.content}</ChainOfThoughtItem>
          )}
        </ChainOfThoughtStep>
      ))}
    </ChainOfThought>
  )
}

export default function MessageList({
  className,
  messages,
  currentResponse,
  currentToolCall,
  isLoading,
  devMode,
  currentThought
}: MessageListProps) {
  const isToolCall = currentResponse.match(/^Calling tools?:\s*\w+/) || currentToolCall
  const isEmpty = !messages.length && !currentResponse
  const isPending = (isLoading && !currentResponse) || currentToolCall

  const messageGroups = useMemo(() => {
    const visibleMessages = devMode ? messages : messages.filter((m) => !isReasoningMessage(m))
    const groups: Array<
      { type: "reasoning"; messages: ChatMessageType[] } | { type: "message"; message: ChatMessageType }
    > = []
    let currentReasoningGroup: ChatMessageType[] = []

    for (const message of visibleMessages) {
      if (message.role === "user" || message.role === "system") {
        if (currentReasoningGroup.length > 0) {
          groups.push({ type: "reasoning", messages: currentReasoningGroup })
          currentReasoningGroup = []
        }
        groups.push({ type: "message", message })
      } else if (isReasoningMessage(message)) {
        currentReasoningGroup.push(message)
      } else {
        if (currentReasoningGroup.length > 0) {
          groups.push({ type: "reasoning", messages: currentReasoningGroup })
          currentReasoningGroup = []
        }
        groups.push({ type: "message", message })
      }
    }
    if (currentReasoningGroup.length > 0) {
      groups.push({ type: "reasoning", messages: currentReasoningGroup })
    }
    return groups
  }, [messages, devMode])

  return (
    <ChatContainerRoot className={cn("min-h-0 flex-1", className)}>
      <ChatContainerContent className={cn("flex flex-col gap-4 m-4", "mx-auto w-full max-w-3xl px-4")}>
        {isEmpty && (
          <div className="py-8 text-center italic text-muted-foreground">Start a conversation with the agent...</div>
        )}

        {messageGroups.map((group) => {
          if (group.type === "reasoning") {
            return <ThoughtChain key={group.messages[0]?.id} messages={group.messages} />
          }
          const { message } = group
          const { role, content } = message
          if (message.clarificationSummary) {
            return (
              <ClarificationSummary
                key={message.id}
                questions={message.clarificationSummary.questions}
                answers={message.clarificationSummary.answers}
              />
            )
          }
          return (
            <ChatMessage key={message.id} messageRole={role}>
              <MessageContent userResponse={role === "user"}>{content}</MessageContent>
            </ChatMessage>
          )
        })}
        {!devMode && (isPending || isToolCall || currentResponse) && (
          <ChatMessage messageRole="assistant" isThinking>
            <ChainOfThought>
              <ChainOfThoughtStep>
                <ChainOfThoughtItem>{currentThought ?? "Thinking..."}</ChainOfThoughtItem>
              </ChainOfThoughtStep>
            </ChainOfThought>
          </ChatMessage>
        )}

        {devMode && !currentResponse && !currentToolCall && isPending && (
          <ChatMessage messageRole="assistant" isThinking>
            <ThoughtSubtitle text={currentThought ?? "Thinking..."} />
          </ChatMessage>
        )}

        {devMode && currentResponse && (
          <ChatMessage messageRole="assistant" isThinking>
            {currentToolCall ? (
              <div className="flex flex-col gap-2">
                <Tool
                  toolPart={{
                    type: currentToolCall?.name ?? "",
                    state: "input-streaming",
                    input: currentToolCall?.arguments ?? {},
                    toolCallId: currentToolCall?.id ?? `pending-${Date.now()}`
                  }}
                  defaultOpen
                />
                {currentThought && <ThoughtSubtitle text={currentThought} />}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <MessageContent isReasoning>{currentResponse}</MessageContent>
                {currentThought && <ThoughtSubtitle text={currentThought} />}
              </div>
            )}
          </ChatMessage>
        )}
        <ChatContainerScrollAnchor />
      </ChatContainerContent>
    </ChatContainerRoot>
  )
}
